import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe2, MapPin, Users, MousePointerClick, Download, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const Bar = ({ rows, testid, emptyLabel }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0)
    return <p className="text-sm text-muted-foreground py-2">{emptyLabel}</p>;
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.name} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="truncate">{r.name}</span>
            <span className="num text-muted-foreground">{r.count}</span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const SOURCE_LABEL = {
  direct: "Langsung / disimpan",
  google: "Google",
  instagram: "Instagram",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  tiktok: "TikTok",
  line: "LINE",
  x: "X (Twitter)",
  youtube: "YouTube",
  internal: "Dalam situs",
  other: "Lainnya",
};

/** Kartu statistik pengunjung website untuk pemilik toko. */
export const VisitorsCard = ({ start, end, onData }) => {
  const [v, setV] = useState(null);
  const [cmp, setCmp] = useState(null);

  useEffect(() => {
    api
      .get("/reports/visitors", { params: { start, end } })
      .then(({ data }) => {
        setV(data);
        onData && onData(data.daily || []);
      })
      .catch(() => setV(null));
    api
      .get("/reports/visitors/compare", { params: { start, end } })
      .then(({ data }) => setCmp(data))
      .catch(() => setCmp(null));
  }, [start, end, onData]);

  const download = async () => {
    try {
      const { data } = await api.get("/reports/visitors/export", {
        params: { start, end },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pengunjung-${start}-${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal mengunduh data pengunjung");
    }
  };

  if (!v) return null;
  const sources = (v.by_source || []).map((r) => ({
    ...r,
    name: SOURCE_LABEL[r.name] || r.name,
  }));

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-5 space-y-5"
      data-testid="visitors-card"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-display text-lg font-semibold">Pengunjung Website</p>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground num">
            {start} — {end}
          </span>
          <Button size="sm" variant="outline" data-testid="export-visitors-btn" onClick={download}>
            <Download size={14} className="mr-1" /> Unduh CSV
          </Button>
        </div>
      </div>
      {cmp && (
        <div
          className="rounded-xl bg-gray-50 px-4 py-3 space-y-1"
          data-testid="visitors-compare"
        >
          <p className="text-sm font-semibold">Bandingkan dengan periode sebelumnya</p>
          <p className="text-xs text-muted-foreground num">
            {cmp.previous_range.start} — {cmp.previous_range.end}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 pt-1">
            {[
              ["Kunjungan", "total"],
              ["Pengunjung unik", "unique"],
            ].map(([label, key]) => {
              const pct = cmp.change_pct[key];
              const up = pct >= 0;
              const Icon = up ? TrendingUp : TrendingDown;
              return (
                <span
                  key={key}
                  className="text-sm flex items-center gap-1.5"
                  data-testid={`visitors-compare-${key}`}
                >
                  {label}:{" "}
                  <b className="num">{cmp.current[key]}</b>
                  <span className="text-muted-foreground num">vs {cmp.previous[key]}</span>
                  <span
                    className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                      up ? "bg-primary/10 text-primary" : "bg-red-50 text-red-600"
                    }`}
                  >
                    <Icon size={12} /> {up ? "+" : ""}
                    {pct}%
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          ["Kunjungan", v.total, Users, "visitors-total"],
          ["Pengunjung unik", v.unique, MousePointerClick, "visitors-unique"],          ["Dari Jepang", v.japan_total, MapPin, "visitors-japan"],
        ].map(([label, value, Icon, testid]) => (
          <div key={testid} className="rounded-xl bg-gray-50 p-4" data-testid={testid}>
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Icon size={14} /> {label}
            </div>
            <p className="num text-2xl font-semibold mt-1">{value}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <MapPin size={14} className="text-primary" /> Kota di Jepang
          </p>
          <Bar rows={v.by_city || []} testid="visitors-by-city" emptyLabel="Belum ada data kota" />
        </div>
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <Globe2 size={14} className="text-primary" /> Negara
          </p>
          <Bar
            rows={v.by_country || []}
            testid="visitors-by-country"
            emptyLabel="Belum ada data negara"
          />
        </div>
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <MousePointerClick size={14} className="text-primary" /> Sumber kunjungan
          </p>
          <Bar rows={sources} testid="visitors-by-source" emptyLabel="Belum ada data sumber" />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Lokasi diperkirakan dari alamat IP (tidak selalu tepat) dan alamat IP disimpan dalam bentuk
        teracak demi privasi pengunjung.
      </p>
      <div className="space-y-2">
        <p className="text-sm font-semibold">Rincian per hari (kota &amp; negara)</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="visitors-daily-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-3 font-medium">Tanggal</th>
                <th className="py-2 pr-3 font-medium text-right">Kunjungan</th>
                <th className="py-2 pr-3 font-medium">Kota (Jepang)</th>
                <th className="py-2 font-medium">Negara</th>
              </tr>
            </thead>
            <tbody>
              {(v.by_day_detail || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-muted-foreground">
                    Belum ada kunjungan pada rentang ini
                  </td>
                </tr>
              )}
              {(v.by_day_detail || []).map((d) => (
                <tr
                  key={d.date}
                  className="border-t border-gray-100 align-top"
                  data-testid={`visitors-day-row-${d.date}`}
                >
                  <td className="py-2 pr-3 num whitespace-nowrap">{d.date}</td>
                  <td className="py-2 pr-3 num text-right">
                    {d.count}
                    <span className="text-muted-foreground"> · {d.unique} unik</span>
                  </td>
                  <td className="py-2 pr-3">
                    {d.cities.length
                      ? d.cities.map((c) => `${c.name} (${c.count})`).join(", ")
                      : "—"}
                  </td>
                  <td className="py-2">
                    {d.countries.length
                      ? d.countries.map((c) => `${c.name} (${c.count})`).join(", ")
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
